import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { MediaItem } from '../../../stores/types/media';
import Icon, { COLORS, ICONS } from '../icon';
import { getVideoTimeAsHHMMSS } from '../../../utils/utils';
import { CreateMediaFromGenerateContentSound } from '../../../connection/media';
import IconToggle from '../icon-toggle';
import useShots from '../../../hooks/use-shots';
import SoundVersionManager from '../../../utils/sound/sound-manager';
import { useEffectiveShotDuration } from '../../../hooks/use-effective-shot-duration';
import { FormContext } from '../../../contexts/generated-content/content-form-context';
import { useGetMediaControls } from '../../../contexts/media-controls';
import { videoActionCommands } from '../../../utils/video-machine';
import { useMediaAudioRestartHook } from './media-audio-restart-hook';
import logger from '../../../utils/logger-rum';

const doFireSync = false;
const { PLAY, PAUSE, TIMECHANGE, SCRUB, STOPSCRUBBING, MUTE, UNMUTE, VOLUME } = videoActionCommands;

interface MediaAudioSyncedProps {
	id: string;
	audioMediaItem: MediaItem;
	loop?: boolean;
	showVersions?: boolean;
	videoSyncMediaItemId: string;
}

export default function MediaAudioSynced({
	id,
	audioMediaItem,
	loop,
	showVersions = false,
	videoSyncMediaItemId,
}: MediaAudioSyncedProps) {
	const token = useSelector((s: any) => s.SessionReducer.token);
	const meetingId = useSelector((s: any) => s.SessionReducer.meeting?.meeting_id);
	const audioMediaItemId = String(audioMediaItem.id);
	const [original, setOriginal] = useState<any>({});
	const [versions, setVersions] = useState([]);
	const [versionsLoading, setVersionsLoading] = useState(false);
	const [showLoopableVersions, setShowLoopableVersions] = useState(false);
	const [showVersionsPanel, setShowVersionsPanel] = useState(false);
	const [selectedVersion, setSelectedVersion] = useState<any>({ audio: audioMediaItem.images });
	const [thumbnailData, setThumbnailData] = useState<string | null>(null);
	const clickRef = useRef<HTMLDivElement>(null);

	const hasVersions = showVersions && audioMediaItem.provider === 'epidemic sound' && audioMediaItem.template_type;

	const { currentShot } = useShots();
	const currentShotDuration = useEffectiveShotDuration(currentShot);
	const soundManager = SoundVersionManager.getInstance();
	const generatedContentFormContext = useContext(FormContext);
	

	const templateControl = useGetMediaControls(videoSyncMediaItemId);
	const audioCanvasControl = useGetMediaControls(audioMediaItemId);

	const {setWatchedId, restartBothAudioAndMedia} = useMediaAudioRestartHook({trigger: (audioCanvasControl?.state?.paused ?? true), templateControl, audioCanvasControl})

	const currentTime = templateControl?.state?.elapsed ?? 0;
	const duration = templateControl?.state?.duration ?? 0;
	const paused = templateControl?.state?.paused ?? true;
	const templateMuted = templateControl?.state?.muted;
	const templateShownVolume = templateControl?.state?.shownVolume;
	const remainingTime = duration - currentTime;

	// Mute mirror — send MUTE/UNMUTE only. Sending VOLUME on an unmute
	// (the old behavior) pushed the video machine's `shownVolume` into the
	// audio machine, clobbering the drawer-set volume whenever
	// useMediaControlsSync propagated the canvas mute through the video.
	useEffect(() => {
		if (!audioCanvasControl?.send) return;
		if (typeof templateMuted === 'undefined') return;
		if (templateMuted) {
			audioCanvasControl.send(MUTE, { doFireSync: false, from: 'media-audio-synced-mute' });
		} else {
			audioCanvasControl.send(UNMUTE, { doFireSync: false, from: 'media-audio-synced-unmute' });
		}
	}, [templateMuted, audioCanvasControl?.send]);

	// Volume mirror — fires only when the video's shownVolume actually changes
	// (e.g. user drags the canvas video volume slider when there's no paired
	// audio target). Decoupled from the mute mirror to avoid resetting the
	// audio's volume during mute/unmute cycles.
	useEffect(() => {
		if (!audioCanvasControl?.send) return;
		if (typeof templateShownVolume === 'undefined') return;
		audioCanvasControl.send(VOLUME, { shownVolume: templateShownVolume, doFireSync: false, from: 'media-audio-synced-volume' });
	}, [templateShownVolume, audioCanvasControl?.send]);

	const radius = 8;

	const playButtonWidth = radius * 11.5 + radius * 2;
	const versionsButtonWidth = hasVersions ? 24 + radius * 1.5 + radius * 2 : 0;
	const totalButtonsWidth = playButtonWidth + versionsButtonWidth;

	const versionTargetDuration = parseInt(
		generatedContentFormContext.statefulFormValues?.seconds
		|| duration
		|| (audioMediaItem.media_data as any)?.original?.config?.duration
		|| currentShotDuration
		|| 30,
	10);

	// Preload thumbnail image
	useEffect(() => {
		if (audioMediaItem.thumbnail_url) {
			fetch(audioMediaItem.thumbnail_url)
				.then(async (response) => {
					const bytes = await response.arrayBuffer();
					const uint8Array = new Uint8Array(bytes);
					let binaryString = '';
					for (let i = 0; i < uint8Array.length; i++) {
						binaryString += String.fromCharCode(uint8Array[i]);
					}
					const base64 = btoa(binaryString);
					const mimeType = response.headers.get('content-type') || 'image/jpeg';
					const data = `data:${mimeType};base64,${base64}`;
					if (thumbnailData !== data)
						setThumbnailData(data);
				})
				.catch(error => {
					logger.error('Error fetching thumbnail:', error);
					setThumbnailData(null);
				});
		}
	}, [audioMediaItem.thumbnail_url, thumbnailData]);

	useEffect(() => {
		if (hasVersions) {
			fetchEpidemicVersion(audioMediaItem);
		}
	}, [hasVersions, showVersions, audioMediaItem.id, audioMediaItem.provider, audioMediaItem.template_type, versionTargetDuration]);

	// Close versions panel when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (clickRef.current && !clickRef.current.contains(event.target as Node)) {
				setShowVersionsPanel(false);
			}
		};

		if (showVersionsPanel) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}

		return undefined;
	}, [showVersionsPanel]);

	const onPlayPauseClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (!templateControl?.send) return;
		const isPaused = templateControl.state?.paused ?? true;
		if (isPaused) {
			templateControl.send(PLAY, { doFireSync, from: 'media-audio-synced' });
			if (templateControl.state?.wasEnded && !audioCanvasControl?.state?.wasEnded) {
				audioCanvasControl?.send?.(TIMECHANGE, {
					currentTime: 0,
					duration: audioCanvasControl?.state?.duration ?? 0,
					doFireSync,
					from: 'media-audio-synced',
				});
			}
		} else {
			templateControl.send(PAUSE, { doFireSync, from: 'media-audio-synced' });
		}
	}, [templateControl, audioCanvasControl]);

	const onScrubStart = useCallback(() => {
		if (!templateControl?.send) return;
		templateControl.send(SCRUB, {
			currentTime: templateControl.state?.elapsed ?? 0,
			duration: templateControl.state?.duration ?? 0,
			from: 'media-audio-synced',
		});
	}, [templateControl]);

	const onScrub = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const nextTime = Number(e.target.value);
		if (!Number.isFinite(nextTime) || !templateControl?.send) return;
		templateControl.send(TIMECHANGE, {
			currentTime: nextTime,
			duration: templateControl.state?.duration ?? 0,
			doFireSync,
			from: 'media-audio-synced',
		});
	}, [templateControl]);

	const onScrubEnd = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
		const nextTime = Number((e.target as HTMLInputElement).value);
		if (!Number.isFinite(nextTime) || !templateControl?.send) return;
		const wasEnded = templateControl.state?.wasEnded ?? false;
		templateControl.send(STOPSCRUBBING, {
			currentTime: nextTime,
			duration: templateControl.state?.duration ?? 0,
			doFireSync,
			from: 'media-audio-synced',
		});
		if (wasEnded) {
			templateControl.send(PLAY, { doFireSync, from: 'media-audio-synced' });
		}
	}, [templateControl]);

	async function handleVersionClick(version: any) {
		if (!version.audio) return;

		if (selectedVersion?.id && version?.id && version.id === selectedVersion.id) {
			restartBothAudioAndMedia();
		} else {
			setSelectedVersion(version);
			setWatchedId(version.id)
			
			if (audioMediaItem && audioMediaItem.id && token && meetingId && original.id && version.id) {
				await CreateMediaFromGenerateContentSound(
					token,
					meetingId,
					original.id,
					version.id,
					audioMediaItem?.id
				);
			}
		}
	}

	function handleSetShowVersionsPanel(e: React.MouseEvent<HTMLElement>) {
		e.stopPropagation();
		if (!showVersionsPanel && audioMediaItem) {
			const version: any = versions.find((version: any) => version.audio === audioMediaItem.images);
			if (version) setShowLoopableVersions(!!version.loopable);
		}
		setShowVersionsPanel(!showVersionsPanel);
	}

	async function fetchEpidemicVersion(mediaItem: MediaItem) {
		try {
			setVersionsLoading(true);
			setVersions([]);
			await soundManager.fetchVersions(
				token,
				mediaItem.template_type || '',
				versionTargetDuration,
				{
					onProgress: (data: any) => {
						if (data.audio && data.audio !== original.audio)
							setOriginal(data);
					},
					onComplete: (data: any) => {
						setOriginal(data);
						if (data.versions && data.versions.length > 0) {
							let actual = data.versions.reduce((sum: number, x: any) => sum + Number(x.duration), 0) / data.versions.length;
							let target = data.versions.reduce((sum: number, x: any) => sum + Number(x.targetDuration), 0) / data.versions.length;
							if (1.0 * actual / target < 0.9)
								setShowLoopableVersions(true);

							setVersions(data.versions);
							setVersionsLoading(false);
						}
					}
				}
			);
		} catch (error) {
			logger.error('Error fetching epidemic sound version:', error);
			setVersionsLoading(false);
		}
	}

	return (
		<div ref={clickRef}>
			<div
				className={`audio-player ${ showVersionsPanel ? 'background-grayscale-1200' : 'background-grayscale-800' } `}
				data-has-versions={hasVersions ? '1' : '0'}
				{...(hasVersions && { onClick: handleSetShowVersionsPanel })}
			>
				<button
					className='background-grayscale-600'
					style={{
						width: `${radius * 11.5}px`,
						height: `${radius * 8}px`,
						borderRadius: `${radius}px`,
						flexShrink: '0',
						marginRight: `${radius * 2}px`,
						...(thumbnailData && {
							backgroundImage: `url(${thumbnailData})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							maskImage: 'linear-gradient(to right, rgba(9,9,10,1) 0%, rgba(9,9,10,0) 100%)',
							maskRepeat: 'no-repeat',
							maskSize: 'cover',
							WebkitMaskImage: 'linear-gradient(to right, rgba(9,9,10,1) 0%, rgba(9,9,10,0) 100%)',
							WebkitMaskRepeat: 'no-repeat',
							WebkitMaskSize: 'cover'
						}),
					}}
					onClick={onPlayPauseClick}
				>
					<Icon
						name={paused ? ICONS.PLAY : ICONS.PAUSE}
						size={24}
						color={COLORS.TEXT_GRAY_100} />
				</button>
				<div className='audio-header-info' style={{ width: `calc(100% - ${totalButtonsWidth}px)`, marginRight: '12px' }}>
					<div className='audio-info'>
						<label style={{
							minWidth: '0',
							overflow: 'hidden',
							textOverflow: 'ellipsis'
						}}>{audioMediaItem.original_name}</label>
						<span style={{
							flexGrow: 1,
							minWidth: `${radius}px`,
						}}></span>
						{versionsLoading ? (
							<label>Loading</label>
						) : (
							<label>-{getVideoTimeAsHHMMSS(remainingTime)}</label>
						)}
					</div>
					<div className="slider-input">
						{versionsLoading ? (
							<div className='slider-loading'></div>
						) : (
							<input
								style={{ margin: '0' }}
								onChange={onScrub}
								onMouseDown={onScrubStart}
								onMouseUp={onScrubEnd}
								type="range"
								min="0"
								max={duration}
								value={currentTime}
								onClick={(e) => e.stopPropagation()}
							/>
						)}
					</div>
				</div>
				{hasVersions && (
				<button
					style={{
						width: '24px',
						height: '24px',
						background: 'transparent',
						marginLeft: `${radius * 1.5}px`,
						marginRight: `${radius * 2}px`,
					}}
					onClick={handleSetShowVersionsPanel}
				>
					<Icon
						name={showVersionsPanel ? ICONS.CARROT_UP : ICONS.CARROT_DOWN}
						size={16}
						color={COLORS.TEXT_GRAY_100} />
				</button>
				)}
			</div>

			{showVersionsPanel && (
				<div className="versions-floating-parent">
					<div className="versions-header">
						<div style={{display: 'flex', flexDirection: 'column'}}>
							<div style={{flexGrow: 1}}></div>
							<Icon name={ICONS.SPARKLE_AI} color={COLORS.TEXT_GRAY_100} size={11.4}></Icon>
							<div style={{flexGrow: 1}}></div>
						</div>
						<div style={{
							marginLeft: '8px',
							flexGrow: 1,
							display: 'flex',
							flexDirection: 'column'
						}} >
							<div style={{flexGrow: 1}}></div>
							<span className='versions-hint'>Generated track options</span>
							<div style={{flexGrow: 1}}></div>
						</div>
						<IconToggle
							onIcon={ICONS.LOOP_TRACK_ON}
							offIcon={ICONS.LOOP_TRACK_OFF}
							size={16}
							checked={showLoopableVersions}
							onChange={setShowLoopableVersions}
						/>
					</div>

					{!versions || !versions.length && (
						<div className="versions-container" data-waiting="1">
							{Array.from({ length: 5 }, (_, index) => (
								<div key={index} className="version-item">
									<div className='version-header'>
										<label className="version-title" aria-hidden="true">ABCDEFGH</label>
										<span style={{flexGrow: 1}}></span>
										<label className="version-time">00:00</label>
									</div>
									<div className="version-waveform-container">
										<div></div>
										<div></div>
									</div>
								</div>
							))}
						</div>
					)}
					{versions && versions.length > 0 && (
						<div className="versions-container" data-waiting="0">
							{versions
								.filter((version: any) => showLoopableVersions == version.loopable)
								.map((version: any) => (
									<div
										key={version.id}
										className="version-item"
										data-selected={selectedVersion?.audio === version.audio ? "1" : "0"}
										onClick={() => handleVersionClick(version)}
									>
										<div className="version-header">
											<span className='version-select-checkbox-icon'>
												<Icon name={ICONS.CHECK_CIRCLE} size={16} color={COLORS.PALE_GREEN} />
											</span>
											<label className="version-title" style={{flexGrow: 1}}>{version.title}</label>
											<label className="version-time">{version.duration ? getVideoTimeAsHHMMSS(version.duration) : ''}</label>
										</div>
										{version.waveform && (
											<div className="version-waveform-container">
												<div className='version-waveform-mask' style={{
													WebkitMaskImage: `url(${version.waveform})`,
													maskImage: `url(${version.waveform})`,
												}}>
													<div className="version-waveform-color"></div>
												</div>
											</div>
										)}
										{version.audio && (
											<audio
												loop={version.loopable}
												crossOrigin={'anonymous'}
												onLoadedMetadata={(e) => {
													const audioElement = e.target as HTMLAudioElement;
													version.duration = audioElement.duration;
												}}
											>
												<source src={version.audio} />
											</audio>
										)}
									</div>
								))
							}
						</div>
					)}
					{original && (
						<div className="versions-container" data-waiting="0" data-original="1">
							<div
								className="version-item"
								data-selected={selectedVersion?.audio === original.audio ? "1" : "0"}
								onClick={() => handleVersionClick(original)}
							>
								<div className="version-header">
									<span className='version-select-checkbox-icon'>
										<Icon name={ICONS.CHECK_CIRCLE} size={16} color={COLORS.PALE_GREEN} />
									</span>
									<label className="version-title" style={{flexGrow: 1}}>Original</label>
									<label className="version-time">{original.duration ? getVideoTimeAsHHMMSS(original.duration) : ''}</label>
								</div>
								{original.waveform && (
									<div className="version-waveform-container">
										<div className='version-waveform-mask' style={{
											WebkitMaskImage: `url(${original.waveform})`,
											maskImage: `url(${original.waveform})`,
										}}>
											<div className="version-waveform-color"></div>
										</div>
									</div>
								)}
								{original.audio && (
									<audio
										loop={original.loopable}
										crossOrigin={'anonymous'}
										onLoadedMetadata={(e) => {
											const audioElement = e.target as HTMLAudioElement;
											original.duration = audioElement.duration;
										}}
									>
										<source src={original.audio} />
									</audio>
								)}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
}
